import {
  TradeType,
  BenchmarkRate,
  CollateralType,
  DeliveryType,
  RateType,
  SettlementVenue,
  getAgreementType,
} from '@/types';
import type { DayCountConvention } from '@/types';
import type { TradeEntryFormData, TradeInventoryItem, TradeCalculations } from './trade-types';

const CUSTODIAN_BASE_ID = 'EXT1111';

// Normalize negative zero (e.g. -1 * 0 = -0 in JS) to positive zero so that
// currency formatting does not produce "-0.00".
const normalizeZero = (n: number): number => (n === 0 ? 0 : n);

// Half-up cents of a float, for the GMRA closing projection (its interest is a
// float anyway). toPrecision(15) strips float noise (1.005 * 100 = 100.49999999999999),
// but a product in the billions has no digits left for the tie, so the cash BO
// books goes through contractValueCents / convertCents instead.
const roundCents = (n: number): number =>
  normalizeZero((Math.sign(n) * Math.round(Number((Math.abs(n) * 100).toPrecision(15)))) / 100);

// ROUND_HALF_UP of num / den on integers, as Decimal.quantize rounds (away from zero).
export const divHalfUp = (num: bigint, den: bigint): bigint => {
  const q = ((num < 0n ? -num : num) + den / 2n) / den;
  return num < 0n ? -q : q;
};

// A form number as an integer; an empty or non-finite field counts as 0.
const toBigInt = (n: number): bigint => BigInt(Number.isFinite(n) ? Math.round(n) : 0);

// Position.save(): quantity × gross_price (2 dp) × margin_bps / 10000, quantized to cents.
const contractValueCents = (quantity: number, grossPrice: number, marginBps: number): bigint =>
  divHalfUp(toBigInt(quantity) * toBigInt(grossPrice * 100) * toBigInt(marginBps), 10_000n);

// Position._to_settlement: cents × fx_rate (6 dp), quantized to cents.
const convertCents = (cents: bigint, fxRate: number): number =>
  normalizeZero(Number(divHalfUp(cents * toBigInt(fxRate * 1e6), 1_000_000n)) / 100);

// Quantize an fx rate to DRF Position.fx_rate precision
// (DecimalField max_digits=10, decimal_places=6).
export const roundFxRate = (n: number): number => Number(n.toFixed(6));

// Quantize an fx rate to a string suitable for DRF submission.
// Returns undefined for non-positive / non-finite values (so the field is
// omitted from the payload instead of submitted as "0", which the backend
// rejects). Also covers the edge case where 0 < input < 5e-7 rounds to 0.
export const toFxRateString = (
  n: number | string | null | undefined,
): string | undefined => {
  if (n === undefined || n === null || n === '') return undefined;
  const num = typeof n === 'number' ? n : parseFloat(n);
  if (!Number.isFinite(num) || num <= 0) return undefined;
  const rounded = roundFxRate(num);
  if (rounded <= 0) return undefined;
  return rounded.toFixed(6);
};

// LR-415: day-count denominator is currency-driven (was benchmark-driven).
// GBP / HKD / JPY → ACT/365; others → ACT/360. Manual override on Position
// (`day_count_convention`) wins when set. Exported so RateDetailsSection can
// reuse the same constant when rendering the "Auto → ACT/N" hint.
export const DAY_COUNT_365_CURRENCIES: ReadonlySet<string> = new Set(['GBP', 'HKD', 'JPY']);
export const dayCountForCurrency = (currency?: string): number =>
  currency && DAY_COUNT_365_CURRENCIES.has(currency.toUpperCase()) ? 365 : 360;
const dayCountFor = (
  currency?: string,
  override?: DayCountConvention | null,
): number => {
  if (override === '360' || override === '365') return Number(override);
  return dayCountForCurrency(currency);
};

// LR-367: mirror of Position.pnl_direction. Unified lend-side / borrow-side:
//   lend-side  = lend (GMSLA) / repo (GMRA)        — we lend shares
//   borrow-side = borrow (GMSLA) / reverse_repo (GMRA) — we borrow shares
// Rebate mode: lend-side = −1 (we pay), borrow-side = +1 (we receive).
// Fee mode:    lend-side = +1 (we earn fee), borrow-side = −1 (we pay fee).
// LR-617: new repo/reverse_repo trades are rebate-only; the repo cells of the
// fee branch stay for amend/detail previews of legacy fee-booked GMRA rows.
const pnlDirection = (
  tradeType?: TradeType,
  rateEntryMode?: 'rebate' | 'fee',
): number => {
  const isLendSide = tradeType === 'lend' || tradeType === 'repo';
  if (rateEntryMode === 'fee') return isLendSide ? 1 : -1;
  // rebate (default)
  return isLendSide ? -1 : 1;
};

const getSubAccountSuffix = (tradeType: TradeType): string => {
  const suffixMap: Record<TradeType, string> = {
    lend: '.001',
    borrow: '.002',
    repo: '.003',
    reverse_repo: '.004',
  };
  return suffixMap[tradeType] || '.001';
};

const generateCounterpartyBaseId = (counterpartyName: string, firmId?: string): string => {
  if (firmId) return firmId;
  const normalized = counterpartyName.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4);
  return `EXT${normalized.padStart(4, '0')}`;
};

/**
 * Calculates trade position values based on form data.
 *
 * Form is margin-only. `position.marginBps` holds margin × 100
 * (e.g. 10000 = 100%, 11000 = 110%). Margin is total collateral required as
 * % of loan value.
 *
 * Formulas:
 * - marginPrice = grossPrice × margin/100
 * - Contract Value = marginPrice × quantity
 * - Net Settlement = quantity × marginPrice (always in security currency)
 * - Collateral Required = netSettlement
 * - Daily Rate = (fixedRate / 100) / dayCount
 * - Expected Return = signed daily PnL × direction (LR-367):
 *     Rebate mode: contractValue × dailyRate × pnlDirection(trade,mode)
 *     Fee mode:    (fee × par − benchmark × CV) / dayCount × pnlDirection(trade,mode)
 *     pnlDirection matrix — unified lend-side / borrow-side axis:
 *       lend-side  (lend GMSLA / repo GMRA)        — we lend shares
 *       borrow-side (borrow GMSLA / reverse_repo GMRA) — we borrow shares
 *       Rebate Lend-side  = −1   Rebate Borrow-side = +1
 *       Fee    Lend-side  = +1   Fee    Borrow-side = −1
 *     GMRA Closing Settlement uses RAW cash-flow interest (no pnlDirection);
 *     differs in sign from totalReturn for scenarios where PnL and cash-flow
 *     legs go opposite ways (e.g. repo + positive rate: we owe more cash on
 *     return, our PnL is negative).
 * - Utilization Rate = (quantity / availableQuantity) × 100
 *
 * Cross-Currency Settlement:
 * - Forward Mode: netSettlementConverted = netSettlement × fxRate
 * - Cash Matching Mode: fxRate = netSettlementConvertedInput / netSettlement (reverse calculation),
 *   then netSettlementConverted = netSettlement × that 6 dp fxRate, not the input
 * - closingSettlementConverted = closingSettlement × fxRate (no user override exists
 *   for the closing leg, so this holds in both modes; same-ccy → undefined)
 *
 * TERM Trade Calculations (when tradeDuration === 'TERM'):
 * - Interest Accrual Days = maturityDate − settlementDate (accrue from SD, exclude maturity)
 * - Total Expected Return = expectedReturn × daysToMaturity
 * - Closing Settlement = netSettlement + totalReturn
 */
export const calculateTradeValues = (formData: Partial<TradeEntryFormData>): TradeCalculations => {
  const quantity = formData.position?.quantity || 0;
  const grossPrice = formData.position?.contractPrice || 0;
  const marginBps = formData.position?.marginBps || 0;
  const rate = formData.rate?.fixedRate || 0;
  const availableQuantity = formData.position?.availableQuantity || 0;
  const securityCurrency = formData.security?.currency || 'USD';
  const settlementCurrency = formData.settlement?.settlementCurrency || securityCurrency;

  const marginPercent = marginBps / 100;
  const marginPrice = grossPrice * (marginPercent / 100);

  const contractValue = marginPrice * quantity;
  const par = grossPrice * quantity;  // LR-367 item 3a: fee leg base in Fee mode.
  // The cash leg BO books: the backend stores contract_value at cents and converts
  // that figure, so every cash amount the form shows starts from it.
  const cvCents = contractValueCents(quantity, grossPrice, marginBps);
  const netSettlement = Number(cvCents) / 100;
  const collateralRequired = netSettlement;
  const dayCount = dayCountFor(securityCurrency, formData.rate?.dayCountConvention);
  const dailyRate = (rate / 100) / dayCount;
  const utilizationRate = availableQuantity > 0 ? (quantity / availableQuantity) * 100 : 0;

  // Daily rate-engine breakdown (mirrors backend Position.cost_of_collateral / fee_income / net_amount).
  // benchmarkRate is resolved from the backend registry by the caller (LR-435).
  const benchmarkPct = formData.rate?.benchmarkRate ?? 0;
  const benchmarkDecimal = benchmarkPct / 100;
  const rateEntryMode = formData.rate?.rateEntryMode ?? 'rebate';
  const spreadBps = formData.rate?.spreadBps ?? 0;
  const feePercent = formData.rate?.feePercent ?? 0;
  // LR-367 item 3a: Fee mode fee_income on par (gross × qty), not CV;
  // cost_of_collateral / spread leg keep CV.
  // LR-464: the rebate spread leg uses |spreadBps| — a special stock now carries
  // a negative spread, but fee_income stays a positive display magnitude
  // (mirrors backend RateDetails.fee_income).
  // LR-631: a fee trade's cost leg is the interest its collateral earns in its
  // cash pool, so it prices on the POOL's rate and the POOL's basis - both
  // supplied by the caller from the selected pool. Everything else (the fee leg,
  // every rebate figure) keeps the trade's own benchmark and day count. Without
  // a pool - a rebate trade, or a fee trade before one is picked - this is the
  // pre-LR-631 formula unchanged.
  const poolRate = rateEntryMode === 'fee' ? formData.rate?.poolRate : undefined;
  const costRateDecimal = poolRate === undefined ? benchmarkDecimal : poolRate / 100;
  const costDayCount = poolRate === undefined ? dayCount : (formData.rate?.poolDayCount ?? dayCount);
  const costOfCollateral = (costRateDecimal * contractValue) / costDayCount;
  const feeIncome =
    rateEntryMode === 'fee'
      ? ((feePercent / 100) * par) / dayCount
      : ((Math.abs(spreadBps) / 10000) * contractValue) / dayCount;
  // LR-367: pnl_direction matrix branches by (tradeType, rateEntryMode).
  const direction = pnlDirection(formData.position?.tradeType, rateEntryMode);
  // LR-395: rebate-mode netAmount mirrors backend Position.net_amount — the
  // full signed rebate cash flow on CV (was the spread leg only). Fee mode
  // unchanged: (fee_income − cost_of_collateral) × direction, par vs CV bases.
  const netAmount =
    rateEntryMode === 'fee'
      ? (feeIncome - costOfCollateral) * direction
      : contractValue * dailyRate * direction;
  // expectedReturn = signed daily PnL; identical to netAmount in both modes
  // (rebate netAmount is now the full-rebate accrual after LR-395).
  const expectedReturn = netAmount;
  let fxRate: number | undefined;
  let netSettlementConverted: number | undefined;
  const isCrossCurrency = settlementCurrency !== securityCurrency;
  const isCashMatchingMode = !!formData.settlement?.netSettlementConvertedInput &&
                              formData.settlement.netSettlementConvertedInput > 0;
  if (isCrossCurrency) {
    if (isCashMatchingMode && netSettlement > 0) {
      const userSettlementCash = formData.settlement!.netSettlementConvertedInput!;
      fxRate = roundFxRate(userSettlementCash / netSettlement);
      // Only the 6 dp rate is booked, not the typed amount, so show what that rate
      // yields; it can be off the input by up to netSettlement × 5e-7.
      netSettlementConverted = convertCents(cvCents, fxRate);
    } else {
      if (formData.settlement?.fxRate && formData.settlement.fxRate > 0) {
        // Round form fxRate symmetrically with the other branches so that
        // calculatedValues.fxRate never carries unrounded precision into
        // downstream consumers (e.g. the cash-matching writeback effect).
        fxRate = roundFxRate(formData.settlement.fxRate);
      }
      // No rate of its own: the booking carries only the form's fx_rate, so a
      // figure converted at any other rate would not be what gets booked.
      netSettlementConverted = fxRate === undefined ? undefined : convertCents(cvCents, fxRate);
    }
  } else {
    fxRate = 1.0;
  }
  const tradeDuration = formData.dates?.tradeDuration;
  const tradeDate = formData.dates?.tradeDate;
  const settlementDate = formData.dates?.settlementDate;
  const maturityDate = formData.dates?.maturityDate;
  const tradeType = formData.position?.tradeType;
  const agreementType = tradeType ? getAgreementType(tradeType) : undefined;

  let daysToMaturity: number | undefined;
  let totalReturn: number | undefined;
  let closingSettlement: number | undefined;
  let closingCashProjected: boolean | undefined;

  // End-of-life date for projecting closing cash:
  //  TERM → maturityDate; OPEN / EVERGREEN-rolling → today (projected).
  let endDate: Date | undefined;
  if (tradeDuration === 'TERM' && maturityDate) {
    endDate = new Date(maturityDate);
  } else if (tradeDate) {
    endDate = new Date();
    closingCashProjected = true;
  }

  // Interest Accrual Days = endDate − settlementDate (accrue from SD, exclude
  // maturity). LR-462: was tradeDate-based, which over-counted by the TD→SD gap.
  // The day count of [SD, maturity) is exactly maturity − SD (last accrual day is
  // maturity − 1), so no extra −1 is needed. Clamp at 0 for a future SD.
  if (settlementDate && endDate) {
    const settlementDateObj = new Date(settlementDate);
    const diffTime = endDate.getTime() - settlementDateObj.getTime();
    daysToMaturity = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
    if (daysToMaturity > 0) {
      totalReturn = expectedReturn * daysToMaturity;
    }
  }

  if (agreementType === 'gmsla') {
    // Scenarios 5–9 (incl. Fee mode): GMSLA closing cash = price × qty × margin_factor.
    // At trade-entry preview time MTM-цены ещё нет, поэтому используем contractValue
    // (= grossPrice × qty × margin_decimal). Бэкенд после booking возьмёт last_marked_price.
    // Interest accrues separately per spec §2e.5-9 / §2g.i.1.b.
    closingSettlement = netSettlement;
  } else if (agreementType === 'gmra' && daysToMaturity !== undefined && daysToMaturity >= 0) {
    // Scenarios 1–4: GMRA closing cash = CV + signed RAW interest (cash-flow direction,
    // NOT our PnL direction). totalReturn already carries pnl_direction, so we recompute
    // the cash-flow leg with raw `dailyRate` × days instead.
    // LR-462: render at 0 days too (degenerate 0-day / pre-settlement projection) —
    // raw interest is then 0, so closing = opening (CV), matching the backend
    // calculate_closing_cash (returns CV at 0 days) instead of showing '-'.
    //   Repo  +rate: closing > CV (we owe more cash back to counterparty).
    //   Repo  −rate: closing < CV (we keep some cash as interest due to us).
    //   ReverseRepo +rate: closing > CV (we receive more cash on return).
    //   ReverseRepo −rate: closing < CV (counterparty keeps some).
    const rawCashFlowInterest = contractValue * dailyRate * daysToMaturity;
    closingSettlement = netSettlement + rawCashFlowInterest;
  }

  // Cross-ccy closing leg, symmetric with netSettlementConverted: the Trade
  // Summary reads this precomputed value instead of multiplying inline. There is
  // no user override for the closing leg, so even in cash-matching mode this is
  // closingSettlement × fxRate at the same rounded rate (same-ccy → undefined).
  // GMSLA closing is the contract value itself, so it converts the same cents.
  const closingSettlementConverted =
    !isCrossCurrency || closingSettlement === undefined || fxRate === undefined
      ? undefined
      : agreementType === 'gmsla'
        ? convertCents(cvCents, fxRate)
        : roundCents(closingSettlement * fxRate);

  // LR-459: settlement-currency equivalents of the per-trade money metrics, at the
  // same resolved fxRate as netSettlementConverted (cross-ccy only → undefined same-ccy).
  // Preview-side mirror of the backend's *_converted fields; the booked figures the
  // detail/blotter/MtM read still come from the backend (FX границы на бэке).
  const marginPriceConverted =
    isCrossCurrency && fxRate !== undefined ? marginPrice * fxRate : undefined;
  const contractValueConverted =
    isCrossCurrency && fxRate !== undefined ? convertCents(cvCents, fxRate) : undefined;
  const expectedDailyReturnConverted =
    isCrossCurrency && fxRate !== undefined ? expectedReturn * fxRate : undefined;

  return {
    contractValue: normalizeZero(netSettlement),
    marginPrice: normalizeZero(marginPrice),
    marginPercent: normalizeZero(marginPercent),
    netSettlement: normalizeZero(netSettlement),
    netSettlementConverted: netSettlementConverted === undefined ? undefined : normalizeZero(netSettlementConverted),
    fxRate,
    collateralRequired: normalizeZero(collateralRequired),
    dailyRate,
    expectedReturn: normalizeZero(expectedReturn),
    utilizationRate,
    daysToMaturity,
    totalReturn: totalReturn === undefined ? undefined : normalizeZero(totalReturn),
    closingSettlement: closingSettlement === undefined ? undefined : normalizeZero(closingSettlement),
    closingSettlementConverted: closingSettlementConverted === undefined ? undefined : normalizeZero(closingSettlementConverted),
    marginPriceConverted: marginPriceConverted === undefined ? undefined : normalizeZero(marginPriceConverted),
    contractValueConverted: contractValueConverted === undefined ? undefined : normalizeZero(contractValueConverted),
    expectedDailyReturnConverted: expectedDailyReturnConverted === undefined ? undefined : normalizeZero(expectedDailyReturnConverted),
    costOfCollateral: normalizeZero(costOfCollateral),
    feeIncome: normalizeZero(feeIncome),
    netAmount: normalizeZero(netAmount),
    closingCashProjected,
  };
};

export const prefillFromInventory = (
  inventoryItem: TradeInventoryItem,
  benchmarkRates: Partial<Record<BenchmarkRate, number>>,
): Partial<TradeEntryFormData> => {
  // UTC and one instant, as in makeCreateTradeFormDefaults.
  const now = new Date();
  const nowIso = now.toISOString();
  const tomorrow = new Date(now.getTime() + 86400000).toISOString().split('T')[0];
  let defaultVenue: SettlementVenue = 'dtc';
  let defaultBenchmark: BenchmarkRate = 'sofr';
  switch (inventoryItem.currency) {
    case 'EUR':
      defaultVenue = 'euroclear';
      defaultBenchmark = 'estr';
      break;
    case 'GBP':
      defaultVenue = 'euroclear';
      defaultBenchmark = 'sonia';
      break;
    // LR-443: tonar/honia are not offered for now, so JPY/HKD have no standard
    // benchmark — default to 'other' (keep in sync with
    // getDefaultBenchmarkByCurrency).
    case 'JPY':
    case 'HKD':
      defaultVenue = 'dtc';
      defaultBenchmark = 'other';
      break;
    default:
      defaultVenue = 'dtc';
      defaultBenchmark = 'sofr';
  }
  return {
    security: {
      symbol: inventoryItem.symbolId,
      shortName: inventoryItem.shortName,
      description: inventoryItem.shortName,
      sedol: inventoryItem.sedol,
      isin: inventoryItem.isin,
      type: inventoryItem.type,
      country: 'US',
      depository: 'DTC',
      currency: inventoryItem.currency,
    },
    account: {
      legalEntity: inventoryItem.legalEntity,
      accountId: inventoryItem.accountId,
    },
    position: {
      tradeType: 'lend' as TradeType,
      collateralType: 'cash' as CollateralType,
      quantity: 0,
      availableQuantity: inventoryItem.quantity,
      contractPrice: inventoryItem.price,
      securityPrice: inventoryItem.price,
      securityPriceCurrency: inventoryItem.currency,
      contractValue: 0,
    },
    rate: {
      rateType: 'floating' as RateType,
      fixedRate: benchmarkRates[defaultBenchmark] ?? 0,
      benchmark: defaultBenchmark,
      benchmarkRate: benchmarkRates[defaultBenchmark] ?? 0,
      dividendPassThrough: 100,
      divFeeShare: 'BORROWER',
    },
    dates: {
      tradeDate: nowIso.slice(0, 10),
      tradeTime: nowIso.slice(11, 19),
      settlementDate: tomorrow,
      tradeDuration: 'OPEN' as const,
      maturityDate: undefined,
      noticePeriodDays: undefined,
    },
    settlement: {
      deliveryType: 'dvp' as DeliveryType,
      venue: defaultVenue,
      settlementCurrency: inventoryItem.currency,
      fxRate: 1.0,
    },
  };
};

export const getSubAccountId = (tradeType: TradeType, counterpartyName?: string, firmId?: string): { custodian: string; counterparty: string } => {
  const suffix = getSubAccountSuffix(tradeType);
  const custodianAccountId = `${CUSTODIAN_BASE_ID}${suffix}`;
  const counterpartyBaseId = counterpartyName ? generateCounterpartyBaseId(counterpartyName, firmId) : 'EXT6666';
  const counterpartyAccountId = `${counterpartyBaseId}${suffix}`;
  return {
    custodian: custodianAccountId,
    counterparty: counterpartyAccountId,
  };
};
